/**
 * The sidebar pane's `···` menu (`specs/M1-B10-shell-chrome.md` §4.2).
 *
 * Measured on monday, `[c:sidebar-workspace-menu]`: **240 px** wide, 32 px rows, 32 px pitch,
 * three groups separated by a 17 px divider —
 *
 *   Manage workspace · Edit workspace · Move workspace · Save as template · Delete workspace
 *   ─
 *   Add new workspace · Browse all workspaces · View archive/trash
 *   ─
 *   monday overview
 *
 * `monday overview` is a monday product surface and is **absent**, per the §3.2 rule the rest
 * of the shell follows.
 *
 * **9j / P-039.** Two things the round-6f capture had not settled, both measured since:
 *   - `Edit workspace` is a **submenu**, not a dialog — a 240 px popover holding
 *     `Rename workspace · Change icon ▸` (`[c:ws-edit-dialog]`, wrapper 256 × 80, radius 8).
 *     The workspace icon is derived from the name here (`WorkspaceAvatar`), so `Change icon`
 *     is a disabled entry with its reason; renaming is real.
 *   - on the account's **Main** (`is_default`) workspace monday greys **Move workspace**,
 *     **Save as template** and **Delete workspace** (`[c:main-ws-sidebar-menu]`, label colour
 *     `rgba(50,51,56,0.38)`; the same menu on a non-default workspace leaves them black).
 *     Delete is refused by `services/workspaces.ts` too — this is the courtesy, not the rule.
 *
 * **6i closed q456.** `Save as template` works: templates are per board in this product (B9),
 * so `workspaces.saveAsTemplate` freezes the workspace's boards through B9's own
 * `templates.save`, one template each, and `WorkspaceTemplateDialog` is the picker.
 * `Move workspace` stays disabled with its reason and is a **registered deviation** — a
 * workspace is top level here, so there is no parent to move one into; monday's own entry
 * moves a workspace between *products* (Work Management / CRM / Dev), and this product is one.
 */
import { useConfirm, useToast } from '@elizmonday/ui';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Menu, type MenuEntry } from '../board-view/table/menus/Menu';
import { workspaceUrl } from '../shell/urls';
import { NameDialog } from './NameDialog';
import { WorkspaceTemplateDialog } from './WorkspaceTemplateDialog';
import { useWorkspace, useWorkspaceMutations } from './workspaces';

export const WORKSPACE_MENU_WIDTH = 240;

export function WorkspaceMenu({
  workspaceId,
  canCreateWorkspace,
  x,
  y,
  onClose,
}: {
  workspaceId: string | null;
  canCreateWorkspace: boolean;
  x: number;
  y: number;
  onClose(): void;
}) {
  const workspace = useWorkspace(workspaceId);
  const mutations = useWorkspaceMutations();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const toast = useToast();
  const [dialog, setDialog] = useState<'rename' | 'create' | 'template' | null>(null);
  /** See `SidebarRowMenu`: `Menu`'s own close runs before the state update lands. */
  const chose = useRef(false);

  const name = workspace.data?.name ?? 'this workspace';
  /** P-039: the account's main workspace is protected, and monday greys these three on it. */
  const isDefault = workspace.data?.isDefault ?? false;
  const mainReason = 'This is the main workspace: it cannot be moved, templated or deleted.';

  /**
   * `Menu` closes itself after a plain entry, and this component owns the menu — so an entry
   * that opens a dialog has to survive that close. Same pattern as `AddMenu`.
   */
  const openDialog = (next: 'rename' | 'create' | 'template') => () => {
    chose.current = true;
    setDialog(next);
  };

  const remove = async () => {
    const ok = await confirm({
      title: `Delete ${name}?`,
      body: 'Every board inside it goes to the trash with it. Both are restorable for 30 days.',
      confirmLabel: 'Delete workspace',
      danger: true,
    });
    if (!ok) return;
    await mutations.remove.mutateAsync(workspaceId ?? '');
    toast(`${name} deleted`, { kind: 'positive' });
    navigate('/');
  };

  if (dialog === 'rename') {
    return (
      <NameDialog
        title="Edit workspace"
        label="Workspace name"
        initial={workspace.data?.name ?? ''}
        confirmLabel="Save"
        testId="edit-workspace-dialog"
        onCancel={onClose}
        onSubmit={(next) => {
          void mutations.update.mutateAsync({ id: workspaceId ?? '', name: next }).then(onClose);
        }}
      />
    );
  }
  if (dialog === 'create') {
    return (
      <NameDialog
        title="Add new workspace"
        label="Workspace name"
        testId="create-workspace-dialog"
        onCancel={onClose}
        onSubmit={(next) => {
          void mutations.create.mutateAsync({ name: next }).then((created) => {
            onClose();
            if (created && typeof created === 'object' && 'id' in created) {
              navigate(workspaceUrl(String((created as { id: string | number }).id)));
            }
          });
        }}
      />
    );
  }

  if (dialog === 'template' && workspaceId !== null) {
    return (
      <WorkspaceTemplateDialog workspaceId={workspaceId} workspaceName={name} onClose={onClose} />
    );
  }

  const entries: MenuEntry[] = [
    {
      id: 'manage',
      label: 'Manage workspace',
      disabled: workspaceId === null,
      onSelect: () => workspaceId && navigate(workspaceUrl(workspaceId)),
    },
    {
      id: 'edit',
      label: 'Edit workspace',
      disabled: workspaceId === null,
      submenuWidth: 240,
      submenu: [
        {
          id: 'edit-rename',
          label: 'Rename workspace',
          'data-testid': 'workspace-menu-rename',
          disabled: workspaceId === null,
          ...(workspaceId === null ? {} : { onSelect: openDialog('rename') }),
        },
        {
          id: 'edit-icon',
          label: 'Change icon',
          disabled: true,
          title:
            'A workspace icon is derived from its name and colour here, so there is nothing to pick (registered deviation, B10 §8).',
        },
      ],
    },
    {
      id: 'move',
      label: 'Move workspace',
      disabled: true,
      title: isDefault
        ? mainReason
        : "A workspace is top level in this product — monday's entry moves one between products, and this is one.",
    },
    {
      id: 'template',
      label: 'Save as template',
      'data-testid': 'workspace-menu-template',
      disabled: workspaceId === null || isDefault,
      title: isDefault
        ? mainReason
        : 'Templates are per board here: every board in the workspace becomes one (B9).',
      ...(workspaceId === null || isDefault ? {} : { onSelect: openDialog('template') }),
    },
    {
      id: 'delete',
      label: 'Delete workspace',
      'data-testid': 'workspace-menu-delete',
      danger: true,
      disabled: workspaceId === null || isDefault,
      ...(isDefault ? { title: mainReason } : {}),
      ...(workspaceId === null || isDefault ? {} : { onSelect: () => void remove() }),
    },
    { id: 'd1', label: '', divider: true },
    {
      id: 'add',
      label: 'Add new workspace',
      disabled: !canCreateWorkspace,
      ...(canCreateWorkspace ? { onSelect: openDialog('create') } : {}),
    },
    { id: 'browse', label: 'Browse all workspaces', onSelect: () => navigate('/workspaces') },
    { id: 'trash', label: 'View archive/trash', onSelect: () => navigate('/archive') },
  ];

  return (
    <Menu
      entries={entries}
      x={x}
      y={y}
      width={WORKSPACE_MENU_WIDTH}
      label="Workspace options"
      onClose={() => {
        // A dialog entry has already swapped the render; only a plain close reaches the parent.
        if (!chose.current) onClose();
      }}
    />
  );
}
